import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import { PrimaryButton } from 'office-ui-fabric-react';
import { DialogType } from 'office-ui-fabric-react/lib/Dialog';
import Main from '../../components/Main';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import PageTitle from '../../components/PageTitle';
import { fetchRoleDetails, deleteRole } from '../../actions/roles';
import { showFlashMessage } from '../../actions/ui';
import styles from './CreateEditRole.style';
import CreateAndEditRoleForm from '../../components/CreateAndEditRoleForm';
import TextButton from '../../components/TextButton';
import SavingIndicator from '../../components/SavingIndicator';
import DeleteDialog from '../../components/DeleteDialog';
import { deleteRoleContent as dialogContent } from '../../constants';

const CreateEditRole = () => {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { pathname, state: locationState } = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [isSaveEnable, setIsSaveEnable] = useState(false);
  const history = useHistory();
  const ref = useRef();

  const roleDetails = slug ? useSelector(state => get(state, `entities.roles.${slug}`)) : null;

  useEffect(() => {
    if (slug && !roleDetails) {
      dispatch(fetchRoleDetails(slug));
      dispatch(fetchRoleDetails('admin'));
    }
  }, [slug]);

  useEffect(() => {
    if (locationState) {
      setIsDialogVisible(locationState.delete);
    } else {
      setIsDialogVisible(false);
    }
  }, [locationState]);

  useEffect(() => {
    if (pathname) {
      setIsDialogVisible(false);
    }
  }, [pathname]);

  const handleCancel = () => {
    history.push({ pathname: '/permissions/roles' });
  };

  const handleUpdate = isEnable => {
    setIsSaveEnable(isEnable);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    await ref.current.submitForm();
    setIsSubmitting(false);
  };

  const handleSuccess = () => {
    if (roleDetails) {
      dispatch(
        showFlashMessage('Role updated successfully', {
          type: 'success',
          duration: 3000,
        }),
      );
    } else {
      dispatch(
        showFlashMessage('Role created successfully', {
          type: 'success',
          duration: 3000,
        }),
      );
    }
    history.push(`/permissions/roles`);
  };

  const handleDismiss = () => {
    history.replace({ state: null });
  };

  const handleDialogSubmit = async () => {
    setIsSubmitting(true);
    const result = await dispatch(deleteRole(slug));
    setIsSubmitting(false);
    setIsDialogVisible(false);
    if (result && !result.error) {
      history.push({ pathname: '/permissions/roles' });
      dispatch(showFlashMessage('Role successfully deleted', { type: 'success', duration: 3000 }));
    }
    dispatch(showFlashMessage(result?.payload?.message, { type: 'error', duration: 3000 }));
  };

  return (
    <>
      <PageTitle>{roleDetails ? 'Edit Role' : 'Create Role'}</PageTitle>

      <Main>
        <Module>
          <ModuleContent>
            <CreateAndEditRoleForm
              ref={ref}
              onUpdate={handleUpdate}
              onSuccess={handleSuccess}
              roleDetails={roleDetails}
            />
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>
          {!isSubmitting ? (
            <PrimaryButton
              type="submit"
              aria-label="Save"
              onClick={handleSave}
              disabled={isSubmitting || !isSaveEnable}
            >
              Save
            </PrimaryButton>
          ) : (
            <SavingIndicator />
          )}
        </div>
      </Main>
      <DeleteDialog
        onDismiss={handleDismiss}
        onSubmit={handleDialogSubmit}
        isRemoving={isSubmitting}
        hidden={!isDialogVisible}
        dialogContentProps={{
          type: DialogType.close,
          title: dialogContent.title,
          closeButtonAriaLabel: 'Close',
        }}
        dialogBodyContent={{
          content: `${dialogContent.content}`,
          buttonText: dialogContent.button,
          showCancelButton: dialogContent.showCancelButton,
        }}
      />
    </>
  );
};

export default CreateEditRole;
